/**
 * telemetry — thin event-tracking seam for the frontend (#1191).
 *
 * **State today: no real sink.** The codebase has no telemetry
 * infrastructure as of this slice — no PostHog, no Amplitude, no
 * Segment, no in-house event store. The Pipeline Integration drawer
 * still wants to know "which CI mode do customers actually copy?"
 * (per the #1191 mockup's note on the Copy button), so we land a
 * `track` shim here. Callers fire `track('pipeline.snippet.copied',
 * {...})` today; the implementation logs at `console.debug` and is
 * otherwise inert.
 *
 * **Why a stub instead of waiting.** Threading the telemetry call
 * site through the Copy button NOW is the cheap half of the work.
 * When a real sink lands (its own meta issue), only this file
 * changes — every call site is already in place. Without the stub
 * the alternative is opening every consumer file later, which is
 * the higher-cost path.
 *
 * **Generic, no vendor names.** The function is `track`, the event
 * names are dot-namespaced strings the call site picks. When a real
 * sink lands the implementation switches to forwarding to whichever
 * SDK we choose; the call sites are untouched. Vendor SDK boundary
 * lives in this file only — see the architectural rail on the
 * server side ("keep vendor names at the SDK boundary only"; the
 * same applies on the frontend).
 */

/** Properties accompanying a telemetry event. Values are simple
 * scalars — the consumer (PostHog / Amplitude / etc.) flattens this
 * shape unchanged. */
export type TelemetryProperties = Record<
  string,
  string | number | boolean | null | undefined
>

/** Fire a telemetry event. Today: logs at `console.debug` for
 * observability during development. When a real sink lands the body
 * of this function changes; call sites do not.
 *
 * Event names use a dot-namespaced shape: `<surface>.<action>` (e.g.
 * `pipeline.snippet.copied`). Properties carry the discriminators
 * that matter for analysis (mode, urlStrategy, etc.). */
export function track(eventName: string, properties?: TelemetryProperties): void {
  // Inert today. Replace with a real SDK forward when one lands.
  // Console-debug surfaces the call during dev so a contributor can
  // see "yes, the event fired" without standing up a sink.
  if (typeof console !== 'undefined' && console.debug) {
    console.debug('[telemetry]', eventName, properties ?? {})
  }
}
